/**
 * RecruiterPricingCard — compact single-plan CTA card built for the
 * /recruiter-visibility funnel.
 *
 * Reuses the same geo-IP currency detection (INR for India, USD
 * everywhere else) and Razorpay checkout as the homepage
 * <PricingSection />, but strips the trust-pillar grid + quarterly
 * cross-sell so the visitor stays laser-focused on the one action:
 * Buy the ₹999 / $49 Full Profile Makeover.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  BadgeCheck,
  Zap,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import {
  fadeUp,
  displaySection,
  kicker,
  sectionWrap,
  sectionInner,
} from './shared';
import {
  detectCurrency,
  fetchServerCurrency,
  openOneTimeCheckout,
} from '../../lib/billing';
import { GuestCheckoutDialog } from '../billing/GuestCheckoutDialog';
import { useAuth } from '../../lib/auth-context';

const BULLETS = [
  'AI + expert résumé makeover — recruiter-friendly formatting',
  'LinkedIn profile rewrite — Headline, About, Experience & Skills',
  'Peer benchmark — see how you stack against top hires in your space',
  'Trend-aware post drafts to build visibility while you interview',
];

export const RecruiterPricingCard = ({ bare = false, id = 'pricing' } = {}) => {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const [currency, setCurrency] = useState(() => detectCurrency());
  const [priceInr, setPriceInr] = useState(999);
  const [priceUsd, setPriceUsd] = useState(49);
  const [guestOpen, setGuestOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Hydrate live plan prices — falls back to defaults on failure.
    import('axios').then(({ default: axios }) => {
      const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
      axios
        .get(`${BACKEND}/api/billing/plans`, { timeout: 8000 })
        .then((res) => {
          if (cancelled) return;
          const plans = res?.data?.plans;
          if (!Array.isArray(plans)) return;
          const p = plans.find((x) => x?.key === 'one_time');
          if (!p) return;
          if (typeof p.price_inr === 'number') setPriceInr(p.price_inr);
          if (typeof p.price_usd === 'number') setPriceUsd(p.price_usd);
        })
        .catch(() => {});
    });
    // Authoritative geoip — overrides the browser-heuristic default.
    fetchServerCurrency().then((geo) => {
      if (cancelled || !geo) return;
      if (geo.currency) setCurrency(geo.currency);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const isInr = currency === 'INR';
  const priceLabel = isInr
    ? `\u20b9${priceInr.toLocaleString('en-IN')}`
    : `$${priceUsd}`;

  const handleBuy = async () => {
    if (busy) return;
    if (!user) {
      setGuestOpen(true);
      return;
    }
    setBusy(true);
    try {
      await openOneTimeCheckout({
        ctaLabel: 'recruiter_visibility_buy_now',
        onSuccess: (result) => {
          const cur = String(result?.currency || currency || 'INR').toUpperCase();
          const region = cur === 'INR' ? 'in' : 'us';
          const amt = result?.amount_paise
            ? Number(result.amount_paise) / 100
            : undefined;
          const params = new URLSearchParams();
          if (result?.plan) params.set('plan', result.plan);
          if (cur) params.set('currency', cur);
          if (amt != null) params.set('amount', String(amt));
          if (result?.order_id) params.set('order_id', result.order_id);
          if (result?.payment_id) params.set('payment_id', result.payment_id);
          navigate(`/thank-you/${region}?${params.toString()}`);
        },
        onDismiss: () => setBusy(false),
      });
    } finally {
      setBusy(false);
    }
  };

  const priceCardJsx = (
    <motion.div
      {...fadeUp}
      className={bare ? 'w-full' : 'mx-auto max-w-2xl'}
      data-testid="recruiter-pricing-card"
    >
      <Card
        // Keep the outer wrapper NON-clipping so the "Full Profile
        // Makeover" ribbon that sits ABOVE the card is never truncated.
        // The subtle radial-blob decoration lives in an inner div with
        // ``overflow-hidden`` so we still get the rounded gradient
        // without eating the ribbon.
        className="relative rounded-[28px] border border-primary/40 bg-card shadow-[0_40px_120px_-60px_hsl(220_40%_2%/0.4)] pt-4"
        data-testid="pricing-tier-one_time"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 overflow-hidden rounded-[28px]"
        >
          <div
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(600px circle at 100% 0%, hsl(212 92% 70% / 0.12), transparent 55%)',
            }}
          />
        </div>
        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full text-[10px] tracking-[0.22em] font-mono uppercase px-3 py-1 whitespace-nowrap z-10 shadow-sm">
          <Sparkles size={11} className="mr-1" aria-hidden="true" /> Full
          Profile Makeover
        </Badge>

        <CardContent className="relative p-7 sm:p-8">
              <div
                className={
                  bare
                    ? 'flex flex-col gap-1'
                    : 'flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4'
                }
              >
                <div className="min-w-0">
                  <p
                    className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground"
                    data-testid="pricing-plan-kicker"
                  >
                    One-time · 15-day access
                  </p>
                  <h3 className={`mt-2 font-semibold tracking-[-0.02em] ${bare ? 'text-xl sm:text-[22px]' : 'text-2xl sm:text-[28px]'}`}>
                    Résumé + LinkedIn Makeover
                  </h3>
                </div>
                <div className={`shrink-0 ${bare ? 'mt-3 text-left' : 'text-left sm:text-right'}`}>
                  <div className={`flex items-end gap-2 ${bare ? '' : 'sm:justify-end'}`}>
                    <span
                      className={`font-display font-semibold tracking-[-0.05em] tabular-nums ${bare ? 'text-[44px] sm:text-5xl' : 'text-5xl sm:text-6xl'}`}
                      data-testid="pricing-plan-price"
                    >
                      {priceLabel}
                    </span>
                    <span className="pb-2 text-sm text-muted-foreground">
                      one-time
                    </span>
                  </div>
                  {isInr ? (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Approx. ${priceUsd} · GST invoice available
                    </p>
                  ) : (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Charged in USD via Razorpay International
                    </p>
                  )}
                </div>
              </div>

              <Separator className="my-6" />

              <ul
                className="grid gap-3"
                data-testid="recruiter-pricing-features"
              >
                {BULLETS.map((b, i) => (
                  <li
                    key={b}
                    className="flex items-start gap-3 text-[13.5px] leading-relaxed"
                    data-testid={`recruiter-pricing-feature-${i}`}
                  >
                    <CheckCircle2
                      size={16}
                      className="text-primary shrink-0 mt-0.5"
                      strokeWidth={2.2}
                      aria-hidden="true"
                    />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={handleBuy}
                disabled={busy}
                className="mt-7 h-12 w-full rounded-full text-[14px]"
                data-testid="recruiter-buy-now-btn"
              >
                {busy ? (
                  <>
                    <Loader2
                      size={15}
                      className="mr-2 animate-spin"
                      aria-hidden="true"
                    />
                    Opening…
                  </>
                ) : (
                  <>
                    Start my makeover — {priceLabel}
                    <ArrowRight size={15} className="ml-2" aria-hidden="true" />
                  </>
                )}
              </Button>

              <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11.5px] text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck
                    size={13}
                    className="text-primary/80"
                    aria-hidden="true"
                  />
                  Razorpay secure
                </span>
                <span aria-hidden="true">·</span>
                <span className="inline-flex items-center gap-1.5">
                  <Zap
                    size={13}
                    className="text-primary/80"
                    aria-hidden="true"
                  />
                  Instant login link
                </span>
                <span aria-hidden="true">·</span>
                <span className="inline-flex items-center gap-1.5">
                  <BadgeCheck
                    size={13}
                    className="text-primary/80"
                    aria-hidden="true"
                  />
                  No auto-renew
                </span>
              </div>
            </CardContent>
          </Card>
    </motion.div>
  );

  return (
    <>
      {bare ? (
        priceCardJsx
      ) : (
        <section
          className={sectionWrap}
          aria-label="Recruiter makeover pricing"
          id={id}
        >
          <div className={sectionInner}>
            <motion.div {...fadeUp} className="mb-10 max-w-2xl">
              <p className={kicker}>The makeover kit</p>
              <h2
                className={`${displaySection} mt-4 text-4xl sm:text-5xl lg:text-[56px]`}
              >
                One evening.
                <br />
                <span className="text-primary">A recruiter-ready you.</span>
              </h2>
              <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
                One flat fee. No subscription. Log in the minute you pay.
              </p>
            </motion.div>
            {priceCardJsx}
          </div>
        </section>
      )}

      <GuestCheckoutDialog
        open={guestOpen}
        onOpenChange={setGuestOpen}
        planLabel="Résumé + LinkedIn Makeover"
        priceLabel={priceLabel}
      />
    </>
  );
};

export default RecruiterPricingCard;
